import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAllVehiclesByCaseId,
  getVehicleById,
  createVehicle,
  updateVehicle,
  deleteVehicle,
  searchVehicles,
  bulkCreateVehicles,
  getVehiclesWithPagination,
  exportVehiclesToExcel,
  formatVehicleData,
  parseAttachmentIds,
  validateVehicleData,
  getMultipleVehicleAssets,
  updateMultipleVehicleAssets,
} from "../../../api/caseVehicleDetailsAsset_api";

// =========== GET ALL VEHICLES BY CASE ID ===========
export const getAllVehiclesByCaseIdAction = createAsyncThunk(
  "vehicle/getAllVehiclesByCaseId",
  async (caseDetailsID, { rejectWithValue }) => {
    try {
      const result = await getAllVehiclesByCaseId(caseDetailsID);

      if (result.success) {
        return {
          caseDetailsID,
          vehicles: result.data,
          assetOwnedBy: result.assetOwnedBy,
          multipleVehicleAssets: result.multipleVehicleAssets || false,
          count: result.count,
          message: result.message
        };
      } else {
        return rejectWithValue({
          message: result.message,
          error: result.error
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: "Failed to fetch vehicles",
        error: error.message
      });
    }
  }
);

// =========== GET SINGLE VEHICLE BY ID ===========
export const getVehicleByIdAction = createAsyncThunk(
  "vehicle/getVehicleById",
  async (vehicleID, { rejectWithValue }) => {
    try {
      const result = await getVehicleById(vehicleID);

      if (result.success) {
        return {
          vehicle: result.data,
          message: result.message
        };
      } else {
        return rejectWithValue({
          message: result.message,
          error: result.error
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: "Failed to fetch vehicle",
        error: error.message
      });
    }
  }
);

// =========== CREATE VEHICLE ===========
export const createVehicleAction = createAsyncThunk(
  "vehicle/createVehicle",
  async ({ caseDetailsID, formData }, { rejectWithValue }) => {
    try {
      let dataToSend;


      if (formData instanceof FormData) {
        dataToSend = formData;

        if (!dataToSend.has('caseDetailsID')) {
          dataToSend.append('caseDetailsID', caseDetailsID);
        }
      } else {

        dataToSend = formatVehicleData({
          ...formData,
          caseDetailsID
        });
      }

      const result = await createVehicle(dataToSend);

      if (result.success) {
        return {
          vehicle: result.data,
          message: result.message
        };
      } else {
        return rejectWithValue({
          message: result.message,
          error: result.error
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: "Failed to create vehicle",
        error: error.message
      });
    }
  }
);

// =========== UPDATE VEHICLE ===========
export const updateVehicleAction = createAsyncThunk(
  "vehicle/updateVehicle",
  async ({ vehicleID, vehicleData, caseDetailsID }, { rejectWithValue }) => {
    try {
      let dataToSend;

      if (vehicleData instanceof FormData) {
        dataToSend = vehicleData;
        if (!dataToSend.has('caseDetailsID') && caseDetailsID) {
          dataToSend.append('caseDetailsID', caseDetailsID);
        }
      } else {

        dataToSend = formatVehicleData({
          ...vehicleData,
          caseDetailsID,
        });
      }

      const result = await updateVehicle(vehicleID, dataToSend);


      if (result.success) {
        return {
          vehicleID,
          vehicle: result.data,
          message: result.message
        };
      } else {
        return rejectWithValue({
          message: result.message,
          error: result.error
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: "Failed to update vehicle",
        error: error.message
      });
    }
  }
);

// =========== DELETE VEHICLE ===========
export const deleteVehicleAction = createAsyncThunk(
  "vehicle/deleteVehicle",
  async ({ vehicleID, updatedBy }, { rejectWithValue }) => {
    try {
      const result = await deleteVehicle(vehicleID, updatedBy);

      if (result.success) {
        return {
          vehicleID,
          message: result.message
        };
      } else {
        return rejectWithValue({
          message: result.message,
          error: result.error
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: "Failed to delete vehicle",
        error: error.message
      });
    }
  }
);

// =========== SEARCH VEHICLES ===========
export const searchVehiclesAction = createAsyncThunk(
  "vehicle/searchVehicles",
  async ({ caseDetailsID, searchCriteria }, { rejectWithValue }) => {
    try {
      const result = await searchVehicles(caseDetailsID, searchCriteria);

      if (result.success) {
        return {
          caseDetailsID,
          vehicles: result.data,
          count: result.count,
          searchCriteria,
          message: result.message
        };
      } else {
        return rejectWithValue({
          message: result.message,
          error: result.error
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: "Failed to search vehicles",
        error: error.message
      });
    }
  }
);

// =========== BULK CREATE VEHICLES ===========
export const bulkCreateVehiclesAction = createAsyncThunk(
  "vehicle/bulkCreateVehicles",
  async (vehiclesData, { rejectWithValue }) => {
    try {

      const formattedVehicles = vehiclesData.map(vehicle => formatVehicleData(vehicle));


      for (const vehicle of formattedVehicles) {
        const validation = validateVehicleData(vehicle);
        if (!validation.isValid) {
          return rejectWithValue({
            message: "Validation failed for one or more vehicles",
            errors: validation.errors
          });
        }
      }

      const result = await bulkCreateVehicles(formattedVehicles);

      if (result.success) {
        return {
          vehicles: result.data,
          message: result.message
        };
      } else {
        return rejectWithValue({
          message: result.message,
          error: result.error
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: "Failed to bulk create vehicles",
        error: error.message
      });
    }
  }
);

// =========== GET VEHICLES WITH PAGINATION ===========
export const getVehiclesWithPaginationAction = createAsyncThunk(
  "vehicle/getVehiclesWithPagination",
  async ({ caseDetailsID, page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const result = await getVehiclesWithPagination(caseDetailsID, page, limit);

      if (result.success) {
        return {
          caseDetailsID,
          vehicles: result.data,
          pagination: result.pagination,
          page,
          limit,
          message: result.message
        };
      } else {
        return rejectWithValue({
          message: result.message,
          error: result.error
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: "Failed to fetch vehicles with pagination",
        error: error.message
      });
    }
  }
);

// =========== EXPORT VEHICLES TO EXCEL ===========
export const exportVehiclesToExcelAction = createAsyncThunk(
  "vehicle/exportVehiclesToExcel",
  async (caseDetailsID, { rejectWithValue }) => {
    try {
      const result = await exportVehiclesToExcel(caseDetailsID);

      if (result.success) {
        return {
          caseDetailsID,
          blobData: result.data,
          message: result.message
        };
      } else {
        return rejectWithValue({
          message: result.message,
          error: result.error
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: "Failed to export vehicles",
        error: error.message
      });
    }
  }
);

// =========== FORMAT VEHICLE DATA (Non-async utility action) ===========
export const formatVehicleDataAction = (vehicleData) => ({
  type: "vehicle/formatVehicleData",
  payload: formatVehicleData(vehicleData)
});

// =========== PARSE ATTACHMENT IDs (Non-async utility action) ===========
export const parseAttachmentIdsAction = (attachmentField) => ({
  type: "vehicle/parseAttachmentIds",
  payload: parseAttachmentIds(attachmentField)
});

// =========== VALIDATE VEHICLE DATA (Non-async utility action) ===========
export const validateVehicleDataAction = (vehicleData) => ({
  type: "vehicle/validateVehicleData",
  payload: validateVehicleData(vehicleData)
});

// =========== CLEAR VEHICLE STATE ===========
export const clearVehicleStateAction = () => ({
  type: "vehicle/clearState"
});

// =========== SET SELECTED VEHICLE ===========
export const setSelectedVehicleAction = (vehicle) => ({
  type: "vehicle/setSelectedVehicle",
  payload: vehicle
});

// =========== SET VEHICLE FILTERS ===========
export const setVehicleFiltersAction = (filters) => ({
  type: "vehicle/setFilters",
  payload: filters
});

// =========== UPDATE MULTIPLE VEHICLE ASSETS ===========
export const updateMultipleVehicleAssetsAction = createAsyncThunk(
  "vehicle/updateMultipleVehicleAssets",
  async ({ caseDetailsID, multipleVehicleAssets }, { rejectWithValue }) => {
    try {
      const result = await updateMultipleVehicleAssets(caseDetailsID, multipleVehicleAssets);
      if (result.success) {
        return {
          caseDetailsID,
          multipleVehicleAssets: result.multipleVehicleAssets,
          message: result.message || "Multiple vehicle assets status updated successfully",
        };
      } else {
        return rejectWithValue({
          message: result.message || "Failed to update multiple vehicle assets",
          error: result.error,
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: "Failed to update multiple vehicle assets",
        error: error.message,
      });
    }
  }
);